import React, { useState } from 'react';
import { useAtom } from 'jotai';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { FieldSelector } from './FieldSelector';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { DraggableFieldKeyChips } from '../DraggableFieldKeyChips';
import { containedButtonStyle } from '@/theme';
import { costLogsTemplateFields, worklogsTemplateFields } from '../../state';
import { abyssColors } from '@/theme/colors';
import { generateMongoObjectId } from '@/utils/databaseHelpers';
import { formatEnumForDisplay } from '@/utils';
import { keyFromTitle } from '../../utils';
import {
  LogSection,
  SectionUnitsEnum,
  TemplateFieldTypeEnum,
  LogsTemplateField,
  LogTemplateType,
} from '@/__generated__/graphql';
import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';

type Mode = 'ReadOnly' | 'Edit';

const SECTION_UNIT_OPTIONS = [
  { label: 'USD', value: SectionUnitsEnum.Usd },
  { label: 'GBP', value: SectionUnitsEnum.Gbp },
  { label: 'Hours', value: SectionUnitsEnum.Hours },
  { label: 'Area', value: SectionUnitsEnum.Area },
  { label: 'N/A', value: SectionUnitsEnum.Na },
];

type DragHandleProps = DraggableAttributes & {
  listeners?: DraggableSyntheticListeners;
};

type SectionCardProps = {
  section: LogSection;
  mode: Mode;
  onChange: (section: LogSection) => void;
  onRemove?: (section: LogSection) => void;
  dragHandleProps?: DragHandleProps;
  hostLogTemplateType: LogTemplateType;
};

const getFieldDescriptionHelperText = (
  fieldDescription: string | null | undefined,
  mode: Mode
): string | undefined => {
  if (mode !== 'Edit') {
    return undefined;
  }

  const description = fieldDescription || '';
  const length = description.length;
  const maxLength = 100;

  if (length > maxLength) {
    return `Description is too long (${length}/${maxLength} characters)`;
  }

  return `${length}/${maxLength} characters`;
};

const DragHandle = ({ dragHandleProps }: { dragHandleProps?: DragHandleProps }) => (
  <IconButton
    size="small"
    sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
    {...dragHandleProps}
  >
    <DragIndicatorIcon />
  </IconButton>
);

export const SectionCard = ({
  section,
  mode,
  onChange,
  onRemove,
  dragHandleProps,
  hostLogTemplateType,
}: SectionCardProps) => {
  const [fields, setFields] = useAtom(
    hostLogTemplateType === LogTemplateType.CostLogTemplate
      ? costLogsTemplateFields
      : worklogsTemplateFields
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const selectedFieldKeys = section.fieldKeys ?? [];
  const allFields = fields ?? [];

  const availableFields = allFields.filter(
    (f) => !selectedFieldKeys.includes(f.key ?? '') && !f.isDerived
  );

  const handleSectionChange = (partial: Partial<LogSection>) => {
    onChange({ ...section, ...partial });
  };

  const handleSelectExistingField = (fieldKey: string) => {
    handleSectionChange({
      fieldKeys: [...selectedFieldKeys, fieldKey],
    });
  };

  const handleCreateNewField = (title: string, description?: string) => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;

    const key = keyFromTitle(trimmedTitle);

    const newField: LogsTemplateField = {
      key,
      name: trimmedTitle,
      type: TemplateFieldTypeEnum.Input,
      sectionKey: key,
      options: [],
      isReadOnly: false,
      isRequired: false,
      serverSide: false,
      id: generateMongoObjectId(),
      fieldDescription: description ?? '',
    };

    const updatedFields = [...allFields, newField];
    setFields(updatedFields);

    handleSectionChange({
      fieldKeys: [...selectedFieldKeys, key],
    });
  };

  const validateNewFieldName = (name: string): string => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      return 'Field name is required';
    }
    if (trimmedName.length < 2) {
      return 'Field name must be at least 2 characters';
    }

    const key = keyFromTitle(trimmedName);
    const existingField = allFields.find((field) => field.key === key);
    if (existingField) {
      return `Field with key "${key}" already exists. Please use a different name.`;
    }

    return '';
  };

  const handleUpdateFieldDescription = (fieldKey: string, description: string) => {
    const updatedFields = allFields.map((field) => {
      if (field.key === fieldKey) {
        return { ...field, fieldDescription: description || '' };
      }
      return field;
    });
    setFields(updatedFields);
  };

  const handleUpdateFieldType = (fieldKey: string, type: TemplateFieldTypeEnum) => {
    const updatedFields = allFields.map((field) => {
      if (field.key === fieldKey) {
        return { ...field, type };
      }
      return field;
    });
    setFields(updatedFields);
  };

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              {mode === 'Edit' && <DragHandle dragHandleProps={dragHandleProps} />}
              {mode === 'Edit' && onRemove && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setIsDeleteDialogOpen(true)}
                  sx={{ '&:hover': { backgroundColor: 'error.light' } }}
                >
                  <DeleteIcon />
                </IconButton>
              )}

              <Stack direction="row" spacing={2} flex={1}>
                <Box flex={2}>
                  {mode === 'Edit' ? (
                    <TextField
                      fullWidth
                      label="Title"
                      value={section.title}
                      onChange={(e) => handleSectionChange({ title: e.target.value })}
                      onBlur={() => {
                        handleSectionChange({
                          key: keyFromTitle(section.title),
                        });
                      }}
                    />
                  ) : (
                    <Typography variant="h4">{section.title}</Typography>
                  )}
                </Box>

                <Box flex={1}>
                  <TextField fullWidth label="Key" disabled value={section.key} />
                </Box>
              </Stack>
            </Stack>

            <FormControl fullWidth size="small">
              <InputLabel>Section Unit</InputLabel>
              <Select
                label="Section Unit"
                value={section.sectionUnit ?? ''}
                disabled={mode === 'ReadOnly'}
                onChange={(e) =>
                  handleSectionChange({
                    sectionUnit: e.target.value as SectionUnitsEnum,
                  })
                }
              >
                {SECTION_UNIT_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={!!section.defaultOpen}
                  disabled={mode === 'ReadOnly'}
                  onChange={(e) =>
                    handleSectionChange({
                      defaultOpen: e.target.checked,
                    })
                  }
                />
              }
              label="Default Open"
            />

            <Box>
              <Typography variant="subtitle2">Fields</Typography>
              {selectedFieldKeys.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No fields selected
                </Typography>
              ) : (
                <DraggableFieldKeyChips
                  items={selectedFieldKeys.map((key) => {
                    const field = allFields.find((f) => f.key === key);
                    return { key, label: field?.name ?? key };
                  })}
                  disabled={mode === 'ReadOnly'}
                  onReorder={(reorderedKeys) => handleSectionChange({ fieldKeys: reorderedKeys })}
                  onRemove={(key) => {
                    const updatedKeys = selectedFieldKeys.filter((k) => k !== key);
                    handleSectionChange({ fieldKeys: updatedKeys });
                  }}
                />
              )}
            </Box>

            {mode === 'Edit' && (
              <FieldSelector
                availableFields={availableFields}
                onSelectExisting={handleSelectExistingField}
                onCreateNew={handleCreateNewField}
                validateNewFieldName={validateNewFieldName}
              />
            )}

            {selectedFieldKeys.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Field Types</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    {selectedFieldKeys.map((key) => {
                      const field = allFields.find((f) => f.key === key);
                      if (!field) return null;

                      return (
                        <Stack key={key} direction="row" alignItems="center" spacing={2}>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{ flexShrink: 0, minWidth: 120 }}
                          >
                            {field.name}
                          </Typography>
                          <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Field Type</InputLabel>
                            <Select
                              label="Field Type"
                              value={field.type}
                              disabled={mode === 'ReadOnly'}
                              onChange={(e) =>
                                handleUpdateFieldType(
                                  field.key!,
                                  e.target.value as TemplateFieldTypeEnum
                                )
                              }
                            >
                              {Object.values(TemplateFieldTypeEnum).map((value) => (
                                <MenuItem key={value} value={value}>
                                  {formatEnumForDisplay(value)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Stack>
                      );
                    })}
                    {mode === 'Edit' && (
                      <Typography
                        variant="body2"
                        color={abyssColors.primary[500]}
                        component="div"
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                          backgroundColor: abyssColors.primary[100],
                          border: `1px solid ${abyssColors.primary[300]}`,
                          borderRadius: 1,
                          p: 1,
                        }}
                      >
                        <InfoOutlined sx={{ mt: '2px', flexShrink: 0 }} fontSize="small" />
                        <span>Select Textarea for comments, e.g. Daily Brief</span>
                      </Typography>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}

            {selectedFieldKeys.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Field Descriptions</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    {selectedFieldKeys.map((key) => {
                      const field = allFields.find((f) => f.key === key);
                      if (!field) return null;

                      return (
                        <TextField
                          key={key}
                          label={`Description for "${field.name}"`}
                          value={field.fieldDescription || ''}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            if (newValue.length <= 100) {
                              handleUpdateFieldDescription(field.key!, newValue);
                            }
                          }}
                          multiline
                          rows={2}
                          placeholder={
                            mode === 'Edit' ? 'Enter field description (optional)' : undefined
                          }
                          size="small"
                          disabled={mode === 'ReadOnly'}
                          error={mode === 'Edit' && (field.fieldDescription || '').length > 100}
                          helperText={getFieldDescriptionHelperText(field.fieldDescription, mode)}
                          slotProps={{
                            input: {
                              readOnly: mode === 'ReadOnly',
                              inputProps: {
                                maxLength: 100,
                              },
                            },
                          }}
                        />
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}
          </Stack>
        </CardContent>
      </Card>
      <GenericDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title="Delete Section"
        content={
          <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
            Are you sure you want to delete this section?
          </Typography>
        }
        actions={
          <>
            <Button
              variant="text"
              onClick={() => setIsDeleteDialogOpen(false)}
              sx={{ color: abyssColors.primary[500] }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                onRemove?.(section);
                setIsDeleteDialogOpen(false);
              }}
              sx={containedButtonStyle}
            >
              Delete
            </Button>
          </>
        }
        actionsStyles={{ mr: 3, pb: 3 }}
      />
    </>
  );
};
